import {
    type ComponentProps,
    type FocusEvent,
    type FormEvent,
    type ReactNode,
    useCallback,
} from 'react';

import type { MaskitoOptions } from '@maskito/core';
import { maskitoInitialCalibrationPlugin } from '@maskito/core';
import { maskitoNumber } from '@maskito/kit';
import { useMaskito } from '@maskito/react';

import { useFieldWiring } from '../hooks/use-field-wiring';
import { cn } from '../lib/utils';
import { Field, FieldLabel, type FieldOverrides, FieldSlot } from './field';
import type { Input } from './input';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from './input-group';

// `minimumFractionDigits: 0` keeps kit's own zero-padding postprocessor a no-op while typing —
// `padFractionDigits` below is what applies the blur-only padding instead.
const NUMBER_MASK_OPTIONS = maskitoNumber({
    decimalSeparator: '.',
    thousandSeparator: ',',
    min: 0,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const CURRENCY_MASK_OPTIONS: MaskitoOptions = {
    ...NUMBER_MASK_OPTIONS,
    // Matches `MaskedInputField`: a seed that already comes masked (`format-rate-for-input.ts`)
    // still passes through calibration once, so it never dirties the form via a mount-time onChange.
    plugins: [...NUMBER_MASK_OPTIONS.plugins, maskitoInitialCalibrationPlugin()],
};

// A single typed fraction digit completes to two on blur; an amount with no fraction digits at
// all stays whole, so "1,000" never becomes "1,000.00".
function padFractionDigits(value: string): string {
    const dotIndex = value.indexOf('.');

    if (dotIndex === -1) return value;

    const fractionDigits = value.length - dotIndex - 1;

    return fractionDigits === 1 ? `${value}0` : value;
}

type Props = Omit<
    ComponentProps<typeof Input>,
    'defaultValue' | 'inputMode' | 'onChange' | 'onInput' | 'type' | 'value'
> & {
    label: string;
    // The column header already labels the field, as in a per-row rate column — keep `label` for
    // the accessible name but drop it from layout instead of repeating it on every row.
    hideLabel?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    description?: string;
    error?: boolean | ReactNode;
    fieldProps?: FieldOverrides;
    fieldClassName?: string;
    inputGroupClassName?: string;
};

function CurrencyInputField({
    label,
    hideLabel,
    value,
    onChange,
    onBlur,
    description,
    error,
    id,
    fieldProps,
    fieldClassName,
    inputGroupClassName,
    className,
    ref,
    ...inputProps
}: Props) {
    const wiring = useFieldWiring({ id, description, error });
    const { className: fieldPropsClassName, ...restFieldProps } = fieldProps ?? {};
    const maskitoRef = useMaskito({ options: CURRENCY_MASK_OPTIONS });

    // Maskito needs the native element to bind to, and the caller may also forward its own `ref`
    // (react-hook-form's `Controller` does). Merge is memoised so neither ref detaches and
    // reattaches on every render.
    const setRef = useCallback(
        (node: HTMLInputElement | null) => {
            maskitoRef(node);

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        },
        [maskitoRef, ref],
    );

    // Maskito mutates the input's DOM value directly and signals the change via a native `input`
    // event rather than React's synthetic `onChange` — see `@maskito/react`'s own guidance.
    function handleInput(event: FormEvent<HTMLInputElement>) {
        onChange?.(event.currentTarget.value);
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
        const padded = padFractionDigits(event.currentTarget.value);

        if (padded !== event.currentTarget.value) {
            onChange?.(padded);
        }

        onBlur?.(event);
    }

    return (
        <Field
            data-disabled={inputProps.disabled}
            data-invalid={wiring.dataInvalid}
            className={cn(fieldPropsClassName, fieldClassName)}
            {...restFieldProps}
        >
            {!hideLabel && <FieldLabel htmlFor={wiring.controlId}>{label}</FieldLabel>}
            <InputGroup className={inputGroupClassName}>
                <InputGroupAddon>
                    <InputGroupText>$</InputGroupText>
                </InputGroupAddon>
                <InputGroupInput
                    {...inputProps}
                    {...wiring.controlProps}
                    // Not an sr-only FieldLabel: FieldLabel's w-fit overrides sr-only's width and widens a scroll container.
                    aria-label={hideLabel ? label : undefined}
                    inputMode="decimal"
                    className={className}
                    value={value ?? ''}
                    onInput={handleInput}
                    onBlur={handleBlur}
                    ref={setRef}
                />
            </InputGroup>
            <FieldSlot
                description={description}
                descriptionId={wiring.descriptionId}
                errorMessage={wiring.errorMessage}
                errorId={wiring.errorId}
            />
        </Field>
    );
}

export { CurrencyInputField };
